/**
 * One mapping from every state the engine has onto four tones.
 *
 * The backend has five separate status enums — campaigns, leads, accounts, scheduled
 * sends and prospect pulls — and an operator reading a screen is asking the same question
 * of all of them: is this working, is it moving, is it broken, or is it at rest. Answering that once,
 * here, is what keeps a badge, a bar segment and a queue tick agreeing about the same
 * row, and what makes a status added backend-side either surface correctly everywhere or
 * fail one obvious switch.
 */

export type Tone = 'signal' | 'live' | 'fault' | 'idle'

export type CampaignStatus =
  | 'draft'
  | 'active'
  | 'paused'
  | 'waiting'
  | 'completed'
  | 'archived'

export type LeadStatus =
  | 'imported'
  | 'queued'
  | 'invited'
  | 'contacted'
  | 'replied'
  | 'completed'
  | 'failed'
  | 'opted_out'

export type AccountStatus =
  | 'pending_auth'
  | 'connected'
  | 'credentials_required'
  | 'disconnected'
  | 'disabled'

export type SendStatus = 'pending' | 'claimed' | 'sent' | 'cancelled' | 'failed'

export type PullStatus =
  | 'waiting'
  | 'pending'
  | 'running'
  | 'ingesting'
  | 'succeeded'
  | 'failed'

/** One column's answer for one row (BIBLE §15). Shares three names with the sends. */
export type CellState =
  | 'empty'
  | 'skipped'
  | 'pending'
  | 'filled'
  | 'nothing'
  | 'failed'
  | 'stale'

export type EngineStatus =
  | CampaignStatus
  | LeadStatus
  | AccountStatus
  | SendStatus
  | PullStatus
  | CellState

const TONES: Record<EngineStatus, Tone> = {
  // Campaigns
  draft: 'idle',
  active: 'live',
  paused: 'signal', // stopped on purpose and waiting for you — not broken, not resting
  // Armed with an empty queue: the next enrolment starts it sending with nobody touching
  // it, which is what `active` is too — the empty queue is not a rest state, just a number
  // shown elsewhere. Not `signal`: that tone means the campaign is waiting on a person,
  // and this one is not. No trigger enrols anybody until the comment webhook lands, so
  // nothing reaches this status today; the tone stays for the state machine that still
  // has it.
  waiting: 'live',
  archived: 'idle',

  // Leads. `replied` is `live` because it is the best thing that happens here.
  imported: 'idle',
  queued: 'signal',
  invited: 'signal',
  contacted: 'signal',
  replied: 'live',
  completed: 'idle',
  failed: 'fault',
  opted_out: 'idle',

  // Accounts
  pending_auth: 'signal',
  connected: 'live',
  credentials_required: 'fault',
  disconnected: 'fault',
  disabled: 'idle', // switched off by an operator, so nothing to fix

  // Scheduled sends
  pending: 'signal',
  claimed: 'signal',
  sent: 'live',
  cancelled: 'idle',

  // Prospect pulls. `running` and `ingesting` are `live` because a paid run really is
  // working; `succeeded` is `idle` because a finished pull is a thing to read, not a
  // thing to watch. (`pending` and `failed` are already above, and mean the same here.)
  running: 'live',
  ingesting: 'live',
  succeeded: 'idle',

  // Cells. `pending` and `failed` are already above and mean here exactly what they mean
  // for a send — a run is on it, or it broke. The rest: nothing has been decided yet
  // (`empty`), the condition said not to bother (`skipped`), it ran and there was nothing
  // there (`nothing`) — all three at rest, none of them a fault. `stale` is `signal`
  // because a person has to decide whether to pay to refill it.
  empty: 'idle',
  skipped: 'idle',
  filled: 'live',
  nothing: 'idle',
  stale: 'signal',
}

/**
 * Which state machine a status belongs to, named only where two of them share a word.
 *
 * `waiting` is the one that collides: a campaign armed with an empty queue is `live` —
 * the next enrolment starts it sending with nobody touching it — while a run chained
 * behind another has not started yet. It has to be `signal`, because the state it moves
 * *into* is `pending`, which is `signal`; toning it `live` would have a run read live,
 * then signal, then live as it went.
 */
export type ToneDomain = 'run'

const BY_DOMAIN: Record<ToneDomain, Partial<Record<string, Tone>>> = {
  run: { waiting: 'signal' },
}

export function stateTone(status: string, domain?: ToneDomain): Tone {
  const override = domain ? BY_DOMAIN[domain][status] : undefined
  return override ?? TONES[status as EngineStatus] ?? 'idle'
}

/** Text and border colours per tone, for badges, dots and rules. */
export const TONE_TEXT: Record<Tone, string> = {
  signal: 'text-signal',
  live: 'text-live',
  fault: 'text-fault',
  idle: 'text-faint',
}

export const TONE_BG: Record<Tone, string> = {
  signal: 'bg-signal',
  live: 'bg-live',
  fault: 'bg-fault',
  idle: 'bg-idle',
}

/**
 * How a status reads to a person. The backend's names are precise and a few of them are
 * jargon; these say the same thing in the words an operator would use out loud.
 *
 * Keyed loosely because it also names things that are not statuses — a send's action type
 * and kind — and those want the same treatment wherever they are shown.
 */
const LABELS: Record<string, string> = {
  credentials_required: 'needs sign-in',
  pending_auth: 'signing in',
  opted_out: 'do not contact',
  connection_invite: 'invite',
  // Cells. "nothing" alone reads as a missing value rather than as an answer, and
  // "stale" is a word about the data where the operator's question is about the column.
  nothing: 'nothing there',
  stale: 'out of date',
}

export function statusLabel(status: string): string {
  return LABELS[status as EngineStatus] ?? status.replace(/_/g, ' ')
}
